import { useLocalStorage } from '@vueuse/core';
import { useLocationStore } from '~/stores/useLocationStore';

const localStorageCart = useLocalStorage('cart', {
  items: [] as CartItem[],
});

export const cartSlideoverOpen = ref(false);

export const cartItemCount = computed(() =>
  localStorageCart.value.items.reduce((sum: number, item: CartItem) => sum + (item.locations?.length ?? 0), 0),
);
export const cartItems = computed(() => localStorageCart.value.items);

const { format } = useMoney();

export const useCart = async () => {
  const toast = useToast();
  const productStore = await useProductStore();
  const locationStore = await useLocationStore();

  const cartProducts: ComputedRef<CartProduct[]> = computed(() => {
    const allProducts = productStore.products;
    const locations = locationStore.locations;

    return localStorageCart.value.items
      .map((cartItem: CartItem) => {
        const product = allProducts.find((p) => p.price.id === cartItem.priceId) ?? null;
        if (!product) {
          localStorageCart.value.items = localStorageCart.value.items.filter((i) => i.priceId !== cartItem.priceId);
          return null;
        }
        const amount = product.locationSelect ? cartItem.locations.length : 1;
        return {
          name: product.name,
          type: product.type,
          amount: amount,
          priceFormatted: format(product.price.unit_amount, product.price.currency),
          price: product.price,
          totalFormatted: format(amount * product.price.unit_amount, product.price.currency),
          locations: locations,
          selectedLocations: locations.filter((l) => cartItem.locations.includes(l.id)),
        };
      })
      .filter((product) => product !== null);
  });

  function updateCart(productPriceId: string, locations: GoogleLocation[]) {
    localStorageCart.value.items.find((cartItem: CartItem) => cartItem.priceId === productPriceId)!.locations = locations.map((l) => l.id);
  }

  const addToCart = function addToCart(product: Product) {
    locationStore.load().then(() => {
      const cartItem: CartItem = {
        priceId: product.price.id,
        locations: locationStore.locations.filter((l) => !l.hasSubscribed.includes(product.type)).map((location: GoogleLocation) => location.id),
      };
      // toast.add({
      //   title: 'Produkt hinzugefügt',
      //   description: `Das Produkt ${product.name} wurde in den Warenkorb gelegt.`,
      // });
      localStorageCart.value.items.push(cartItem);
      cartSlideoverOpen.value = true;
    });
  };

  // function addToCartWithLocation(productType: SubscriptionProductType, locationId: number) {
  const addToCartWithLocation = async (productType: SubscriptionProductType, locationId: number) => {
    await productStore.load();
    const product = productStore.products.find((p) => p.type === productType);
    if (!product) {
      console.error(`Product "${productType}" not found in store`);
      return;
    }
    const location = locationStore.locations.find((l) => l.id === locationId);
    if (location?.hasSubscribed.includes(productType)) {
      console.error('Location has already subscribed to this product type');
      return;
    }
    const cartItem = cartItems.value.find((item) => item.priceId === product.price.id);
    if (cartItem) {
      if (!cartItem.locations.includes(locationId)) {
        cartItem.locations.push(locationId);
      }
    } else {
      const newCartItem: CartItem = {
        priceId: product.price.id,
        locations: [locationId],
      };
      localStorageCart.value.items.push(newCartItem);
    }
    cartSlideoverOpen.value = true;
  };

  const removeFromCart = (priceId: string) => {
    localStorageCart.value.items = localStorageCart.value.items.filter((p) => p.priceId !== priceId);
    const product = productStore.products.find((p: Product) => p.price.id === priceId) ?? null;
    if (product === null) {
      console.error('Product not found in store');
      return;
    }
    toast.add({
      title: 'Produkt entfernt',
      description: `Das Produkt ${product.name} wurde aus dem Warenkorb entfernt.`,
    });
  };

  const sanitize = async () => {
    await productStore.load();
    const ids: string[] = productStore.products.map((p) => p.price.id);
    // remove unavailable products/prices from cart
    localStorageCart.value.items = localStorageCart.value.items.filter((cartItem: CartItem) => {
      return ids.includes(cartItem.priceId);
    });

    // remove unavailable or subscribed locations from products/prices
    await locationStore.load();
    localStorageCart.value.items = localStorageCart.value.items.map((cartItem: CartItem) => {
      const product = productStore.products.find((p: Product) => p.price.id === cartItem.priceId) ?? null;
      const availableLocationIds = locationStore.locations
        .filter((l) => !l.hasSubscribed.includes(product!.type))
        .map((location: GoogleLocation) => location.id);
      cartItem.locations = cartItem.locations.filter((locationId: number) => availableLocationIds.includes(locationId));

      return cartItem;
    });

    // remove products with no locations
    localStorageCart.value.items = localStorageCart.value.items.filter((i) => i.locations.length > 0);
  };

  const cartCheckoutButtonClickable = computed(() => {
    return (
      localStorageCart.value.items.length > 0 &&
      localStorageCart.value.items.filter((item: CartItem) => item.locations.length > 0).length === localStorageCart.value.items.length
    );
  });

  const clearCart = () => {
    localStorageCart.value = {
      items: [] as CartItem[],
    };
    locationStore.reload();
  };

  function cartContains(productType: SubscriptionProductType, locationId?: number): boolean {
    const product = productStore.products.find((p) => p.type === productType);
    if (!product) return false;
    const cartItem = localStorageCart.value.items.find((i) => i.priceId === product.price.id);
    if (!cartItem) return false;
    if (locationId === undefined) return true;
    return cartItem.locations.includes(locationId);
  }

  function canBeAddedToCart(priceId: string, productType: SubscriptionProductType): boolean {
    const locations = locationStore.locations.filter((l) => !l.hasSubscribed.includes(productType));
    return locations.length > 0 && localStorageCart.value.items.find((p) => p.priceId === priceId) === undefined;
  }

  const validateCart = async (): Promise<unknown> => {
    return useApi().post('/shop/cart', { cartItems: localStorageCart.value.items });
  };

  const cartItems: ComputedRef<CartItem[]> = computed(() => {
    return localStorageCart.value.items;
  });

  return {
    cartItemCount,
    updateCart,
    addToCart,
    addToCartWithLocation,
    cartProducts,
    removeFromCart,
    cartContains,
    canBeAddedToCart,
    sanitize,
    cartCheckoutButtonClickable,
    clearCart,
    validateCart,
    cartItems,
  };
};
